import type { Pregunta, RespuestaResumible } from '@copathy/shared';
import {
  OBSERVACION_ROTULO_POR_OMISION,
  abreEnPantallaCompleta,
  etiquetaCabeEnPastilla,
  resumenDeRespuesta,
} from '@copathy/shared';

/**
 * Las respuestas **de la previa del constructor**, y su resumen en una línea.
 *
 * ── Por qué la previa guarda respuestas ──
 * El recorrido que se eligió (CC-12) es una **lista con las preguntas plegadas,
 * mostrando lo que ya se respondió**. Sin respuestas no hay nada que plegar: la
 * previa tendría que dibujar filas vacías y quien arma el cuestionario no podría
 * ver si «De acuerdo» cabe en una línea o si su matriz de seis afirmaciones se
 * resume en algo legible. O sea que **el resumen por tipo es el trabajo de
 * CC-12**, no un adorno del prototipo.
 *
 * ⚠️ **Esto vive y muere en el navegador.** No sale a ninguna parte, no se envía
 * y no toca la base: la previa no es el formulario. El envío de verdad lo hace la
 * app con `esquemaEnviarRespuestas`, que tiene otra forma —una fila por opción
 * marcada, una por afirmación de matriz— porque va a una base con restricciones.
 * Reutilizar esa forma acá obligaría a construir media validación del servidor
 * para dibujar un resumen.
 *
 * ⚠️ **La forma y el resumen ya no se escriben acá**: son los de
 * `shared/recorrido`, más la observación. Estaban duplicados —esta mitad en la
 * web, la otra en la app— y ese es el defecto que no falla ninguna prueba: la
 * previa resumía «De acuerdo» y el teléfono podía decir «Punto 4» (D-149).
 */
export type RespuestaLocal = RespuestaResumible & {
  /** La observación abierta (035). Va aparte del valor: son dos filas en la base. */
  observacion?: string;
};

export type Respuestas = Record<number, RespuestaLocal | undefined>;

/** ¿Se respondió lo esencial de esta pregunta? La observación NO cuenta. */
export function estaRespondida(p: Pregunta, r: RespuestaLocal | undefined): boolean {
  if (!r) return false;
  switch (p.tipo) {
    case 'likert':
    case 'barra':
      return r.punto !== undefined;
    case 'opcion_unica':
      return r.opcion !== undefined;
    case 'opcion_multiple':
      return (r.opciones?.length ?? 0) > 0;
    case 'si_no':
      return r.bool !== undefined;
    case 'texto_corto':
    case 'texto_largo':
      return (r.texto ?? '').trim().length > 0;
    case 'numero':
      return (r.numero ?? '').trim().length > 0;
    case 'fecha':
      return (r.fecha ?? '').length > 0;
    case 'matriz_likert':
      /*
        Una matriz está respondida cuando lo están TODAS sus afirmaciones. A
        medias no vale: en el envío de verdad cada afirmación es su propia fila, y
        una matriz de seis con cuatro contestadas dejaría dos huecos que el
        agregado contaría como «no opinó» sin que la persona lo supiera.
      */
      return p.items.length > 0 && p.items.every((i) => r.matriz?.[i.id] !== undefined);
    case 'archivo':
    case 'nota_voz':
    case 'video':
      return r.adjunto !== undefined;
    case 'seccion':
      return true;
  }
}

/** El rótulo del campo de observación, con su valor por omisión. */
export const rotuloObservacion = (p: Pregunta) =>
  p.observacionRotulo?.trim() || OBSERVACION_ROTULO_POR_OMISION;

/*
  Reexportados para que la fila plegada no importe de tres sitios. `resumenDeRespuesta`
  y `abreEnPantallaCompleta` **ya no se implementan acá**: viven en `shared/recorrido`,
  porque la app tiene que resumir igual que la previa (D-149).
*/
export { abreEnPantallaCompleta, etiquetaCabeEnPastilla, resumenDeRespuesta };
